import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Copy, Gift, Wallet } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useTheme } from '@/contexts/ThemeContext';
import { useCopyToClipboard } from '@/hooks/useCopyToClipboard';
import type { DiscordLinkOffer, DiscordLinkReward } from '@/lib/services/discord.service';

/** Valor em créditos na moeda da loja, como o resto da vitrine escreve. */
export function useFormatCredits() {
  const { config } = useTheme();
  return (valor: number) =>
    new Intl.NumberFormat(config?.language || 'pt-BR', {
      style: 'currency',
      currency: config?.currency?.code || 'BRL',
    }).format(valor);
}

/**
 * A recompensa que o jogador ganhou por vincular o Discord.
 *
 * Location: src/components/discord
 * Why: aparece no modal de confirmação e na aba Discord do perfil. O código
 * do presente fica à vista aqui também: a mensagem direta do bot pode não
 * chegar (privado fechado), e o presente não pode depender dela.
 */
export function DiscordRewardInfo({ reward }: { reward: DiscordLinkReward }) {
  const { t } = useTranslation();
  const { copy } = useCopyToClipboard();
  const formatar = useFormatCredits();

  if (reward.type === 'CREDIT') {
    return (
      <div className="flex items-start gap-3 rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-4 text-left" data-testid="discord-reward">
        <Wallet className="mt-0.5 h-5 w-5 shrink-0 text-emerald-500" />
        <div className="space-y-2">
          <p className="font-semibold">{t('store.discord.reward_credit', { amount: formatar(reward.credits) })}</p>
          <Button asChild size="sm" variant="outline">
            <Link to="/perfil?tab=credits">{t('store.discord.reward_credit_button')}</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-start gap-3 rounded-xl border border-[#5865F2]/30 bg-[#5865F2]/10 p-4 text-left" data-testid="discord-reward">
      <Gift className="mt-0.5 h-5 w-5 shrink-0 text-[#5865F2]" />
      <div className="min-w-0 space-y-2">
        <p className="font-semibold">{t('store.discord.reward_gift', { product: reward.productName ?? '' })}</p>
        {reward.code && (
          <div className="flex flex-wrap items-center gap-2">
            <code className="rounded-md bg-background px-2 py-1 font-mono text-sm">{reward.code}</code>
            <Button
              size="icon"
              variant="ghost"
              className="h-8 w-8"
              aria-label={t('store.discord.reward_copy')}
              onClick={() => {
                copy(reward.code ?? '');
                toast.success(t('store.discord.reward_copied'));
              }}
            >
              <Copy className="h-4 w-4" />
            </Button>
            {reward.redeemed && <Badge variant="secondary">{t('store.discord.reward_redeemed')}</Badge>}
          </div>
        )}
        {!reward.redeemed && reward.code && (
          <>
            <p className="text-sm text-muted-foreground">{t('store.discord.reward_gift_hint')}</p>
            <Button asChild size="sm" className="bg-[#5865F2] text-white hover:bg-[#4752c4]">
              <Link to={`/presentes?codigo=${encodeURIComponent(reward.code)}`}>{t('store.discord.reward_gift_button')}</Link>
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

/** O convite antes de vincular: "vincule e ganhe". */
export function DiscordOfferInfo({ offer }: { offer: DiscordLinkOffer }) {
  const { t } = useTranslation();
  const formatar = useFormatCredits();

  return (
    <div className="flex items-center gap-3 rounded-xl border border-[#5865F2]/30 bg-[#5865F2]/10 p-3 text-sm" data-testid="discord-offer">
      <Gift className="h-5 w-5 shrink-0 text-[#5865F2]" />
      <span>
        {offer.type === 'CREDIT'
          ? t('store.discord.offer_credit', { amount: formatar(offer.credits) })
          : t('store.discord.offer_gift', { product: offer.productName })}
      </span>
    </div>
  );
}
